<script setup lang="ts">
import { Role } from '@/models/user/Role'
import { onBeforeMount, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useHTTP } from '@/plugins/http'
import { useI18n } from 'vue-i18n'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import { ErrorResponse } from '@/types/types'
import { Route } from '@/router/routes'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Brand from '@/models/inventory/Brand'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'

const { t } = useI18n({
  useScope: 'global',
  messages: {
    en: {
      arName: 'Arabic Name',
      enName: 'English Name',
      arDescription: 'Arabic Description',
      enDescription: 'English Description',
      newRole: 'Create new role'
    },
    ar: {
      arName: 'الاسم بالعربي',
      enName: 'الاسم بالانجليزي',
      arDescription: 'الوصف بالعربي',
      enDescription: 'الوصف بالانجليزي',
      newRole: 'انشاء منصب جديد'
    }
  }
})

const http = useHTTP()
const router = useRouter()

const loading = ref(false)
const error = ref({ title: '', body: '' })

const roles = ref<Role[]>()

const fetchRoles = async () => {
  try {
    loading.value = true
    roles.value = await http.get<Role[]>(Role.ENDPOINT)
  } catch (errorResp) {
    const es = errorResp as ErrorResponse
    error.value.title = es.title
    error.value.body = es.body || ''
  } finally {
    loading.value = false
  }
}

onBeforeMount(async () => {
  await fetchRoles()
})

const goToRole = (role: Role) => {
  router.push({ name: 'role', params: { id: role.id } })
}

const goToNewRole = () => {
  router.push(Route.HR_ROLES_NEW_ROLE)
}

const { showSuccess, showDanger } = useAlertModal()

const deleteRole = async (role: Role) => {
  if (confirm(t('views.common.listView.selectedRows.deleteConfirm'))) {
    try {
      await http.delete(Role.ENDPOINT, { data: [role] })
      showSuccess({
        title: t('views.common.listView.selectedRows.deleteSuccess')
      })
      await fetchRoles()
    } catch (error) {
      const errorResp = error as ErrorResponse
      showDanger({
        title: errorResp.title,
        body: errorResp.body
      })
    }
  }
}
</script>

<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div class="overflow-x-auto mx-5" v-else>
    <table class="min-w-full divide-y-2 divide-gray-200 bg-white text-sm">
      <thead class="ltr:text-left rtl:text-right">
        <tr>
          <th class="whitespace-nowrap px-4 py-2 font-medium text-gray-900">{{ t('arName') }}</th>
          <th class="whitespace-nowrap px-4 py-2 font-medium text-gray-900">{{ t('enName') }}</th>
          <th class="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
            {{ t('arDescription') }}
          </th>
          <th class="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
            {{ t('enDescription') }}
          </th>
        </tr>
      </thead>

      <tbody class="divide-y divide-gray-200">
        <tr class="hover:bg-gray-200 cursor-pointer" v-for="role in roles" :key="role.id">
          <td
            class="whitespace-nowrap px-4 py-2 font-medium text-gray-900"
            @click.prevent="goToRole(role)"
          >
            {{ role.arName }}
          </td>
          <td class="whitespace-nowrap px-4 py-2 text-gray-700" @click.prevent="goToRole(role)">
            {{ role.enName }}
          </td>
          <td class="whitespace-nowrap px-4 py-2 text-gray-700" @click.prevent="goToRole(role)">
            {{ role.arDescription }}
          </td>
          <td class="whitespace-nowrap px-4 py-2 text-gray-700" @click.prevent="goToRole(role)">
            {{ role.enDescription }}
          </td>
          <td
            class="whitespace-nowrap px-4 py-2"
            @click.prevent="deleteRole(role)"
            v-if="$can('delete', '/user/roles') && role.enName !== 'owner'"
          >
            <font-awesome-icon icon="fa-trash" class="text-red-500"></font-awesome-icon>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="w-full flex justify-center mt-2 p-2" v-if="$can('create', '/user/roles')">
      <a
        class="inline-block rounded border border-blue-600 px-12 py-3 text-sm font-medium text-blue-600 hover:bg-blue-600 hover:text-white focus:outline-none focus:ring active:bg-blue-500 cursor-pointer"
        @click.prevent="goToNewRole"
      >
        {{ t('newRole') }}
      </a>
    </div>
  </div>
</template>
